import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  ChevronDown, 
  ChevronUp, 
  HelpCircle, 
  Video, 
  FileText, 
  Lightbulb,
  ExternalLink
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import {
  createSupportTicket,
  newSubmissionKey,
  syncTicketToHelpDesk,
} from "@/lib/supportTicket";

interface WorkflowGuidancePanelProps {
  workflowId: string;
  currentPhaseNumber: number;
  taskId: string;
  unaId: string;
}

export const WorkflowGuidancePanel = ({
  workflowId,
  currentPhaseNumber,
  taskId,
  unaId,
}: WorkflowGuidancePanelProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [phaseGuidance, setPhaseGuidance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [sendingHelpState, setSendingHelpState] = useState(false);
  const sendingHelp = useRef(false);
  const helpKey = useRef(newSubmissionKey());

  useEffect(() => {
    loadPhaseGuidance();
  }, [workflowId, currentPhaseNumber]);

  const loadPhaseGuidance = async () => {
    try {
      const { data: workflow, error } = await supabase
        .from('workflows')
        .select('phases, guidance_notes')
        .eq('id', workflowId)
        .single();

      if (error) throw error;

      if (workflow?.phases) {
        const phases = workflow.phases as any[];
        const currentPhase = phases.find(p => p.phase_number === currentPhaseNumber);
        setPhaseGuidance(currentPhase);
      }
    } catch (error) {
      console.error('Error loading phase guidance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGetHelp = async () => {
    // Belt and braces next to the disabled button: blocks a double fire even if
    // the click lands before React repaints.
    if (sendingHelp.current) return;
    sendingHelp.current = true;
    setSendingHelpState(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) return;

      // Get task details for context
      const { data: task } = await supabase
        .from('custom_tasks')
        .select('task_title, task_description')
        .eq('id', taskId)
        .single();

      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .maybeSingle();

      const subject = `Help with Workflow: ${phaseGuidance?.phase_name || 'Phase ' + currentPhaseNumber}`;
      const message = `I need help with the following workflow phase:\n\nTask: ${task?.task_title}\nPhase: ${phaseGuidance?.phase_name}\n\nTask ID: ${taskId}\nWorkflow ID: ${workflowId}`;

      // One key for this request, held across retries so a second attempt
      // reuses the first attempt's ticket instead of opening another one.
      const ticket = await createSupportTicket({
        userId: user.id,
        subject,
        message,
        category: 'workflow_help',
        idempotencyKey: helpKey.current,
      });

      // Only hand it to the help desk once the row is safely saved.
      syncTicketToHelpDesk({
        ticketId: ticket.id,
        subject,
        message,
        userEmail: profile?.email || user.email || '',
        userName: profile?.full_name || 'Member',
        category: 'workflow_help',
        priority: 'medium',
      });

      helpKey.current = newSubmissionKey();
      toast({
        title: "Help Request Sent",
        description: "A support specialist will reach out to you soon",
      });
    } catch (error) {
      console.error('Error creating support ticket:', error);
      toast({
        title: "Error",
        description: "Failed to send help request",
        variant: "destructive",
      });
    } finally {
      sendingHelp.current = false;
      setSendingHelpState(false);
    }
  };

  if (loading || !phaseGuidance) {
    return null;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-blue-200 dark:border-blue-800">
        <CardHeader className="pb-3">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
              <CardTitle className="text-base flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-blue-500" />
                How to Complete This Phase
              </CardTitle>
              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="space-y-4 pt-0">
            <Separator />

            {/* Phase Description */}
            {phaseGuidance.description && (
              <div>
                <h4 className="font-medium text-sm mb-2">Overview</h4>
                <p className="text-sm text-muted-foreground">{phaseGuidance.description}</p>
              </div>
            )}

            {/* Steps */}
            {phaseGuidance.tasks && phaseGuidance.tasks.length > 0 && (
              <div>
                <h4 className="font-medium text-sm mb-2">Step-by-Step Instructions</h4>
                <ol className="space-y-2 text-sm">
                  {phaseGuidance.tasks.map((task: string, idx: number) => (
                    <li key={idx} className="flex gap-2">
                      <span className="font-medium text-primary">{idx + 1}.</span>
                      <span>{task}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Training Videos */}
            {phaseGuidance.training_videos && phaseGuidance.training_videos.length > 0 && (
              <div>
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <Video className="h-4 w-4 text-red-500" />
                  Training Videos
                </h4>
                <div className="space-y-2">
                  {phaseGuidance.training_videos.map((videoUrl: string, idx: number) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      className="w-full justify-start gap-2"
                      onClick={() => window.open(videoUrl, '_blank')}
                    >
                      <Video className="h-4 w-4" />
                      <span className="text-xs">Watch Tutorial {idx + 1}</span>
                      <ExternalLink className="h-3 w-3 ml-auto" />
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Common Questions */}
            <div>
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-orange-500" />
                Common Questions
              </h4>
              <div className="text-sm text-muted-foreground space-y-2">
                <p>• What if I don't have all the required information?</p>
                <p>• Can I save my progress and come back later?</p>
                <p>• How long should this phase take?</p>
                <p 
                  className="text-xs text-blue-600 dark:text-blue-400 mt-2 cursor-pointer hover:underline"
                  onClick={() => navigate('/support/library')}
                >
                  View all FAQs →
                </p>
              </div>
            </div>

            <Separator />

            {/* Help Button */}
            <Button 
              variant="outline" 
              className="w-full gap-2"
              onClick={handleGetHelp}
              disabled={sendingHelpState}
            >
              <HelpCircle className="h-4 w-4" />
              Need Help? Contact Support
            </Button>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};
